/*
 * After a user changes a play input value, these include any immediate changes to other inputs.
 * These changes can be overwritten if the user updates the affected input.
 */

import { isDisplayable } from "util/misc";
import { isPlayInterruption, fieldExistsOnPlayType } from "games/utils";
import _ from "lodash";

const lengthOfPlayFields = [
  "yardLine",
  "yardLineTeamId",
  "ballSpot",
  "ballSpotTeamId",
  "teamInPossessionId",
  "includesScore"
];

/*
 * If a play’s length is greater or equal to the distance to go, it converts a first down.
 * Unless the distance to go = “goal”, in which case it can’t convert.
 * If a Penalty Defense play type advances the ball beyond the distance to go,
 * a first down is given to the offense.
 */
const getconvertsFirstDown = (fieldName, distanceToGo, lengthOfPlay, play) => {
  const noRelevantFieldsChanged =
    !lengthOfPlayFields.includes(fieldName) && fieldName !== "distanceToGo";
  if (noRelevantFieldsChanged || fieldName === "convertsFirstDown")
    return play.convertsFirstDown;
  return lengthOfPlay >= distanceToGo;
};

const getLengthOfPlay = (fieldName, play) => {
  if (!lengthOfPlayFields.includes(fieldName)) return play.lengthOfPlay;

  const {
    yardLine,
    yardLineTeamId,
    ballSpot,
    ballSpotTeamId,
    teamInPossessionId,
    playType,
    includesScore
  } = play;

  const hasAllRelatedValues =
    isDisplayable(yardLine) &&
    isDisplayable(yardLineTeamId) &&
    isDisplayable(ballSpot) &&
    isDisplayable(ballSpotTeamId);

  const hasAllRelatedFieldGoalValues =
    isDisplayable(yardLine) && isDisplayable(yardLineTeamId);

  const dataIsMissing =
    (playType === "fieldGoal" && !hasAllRelatedFieldGoalValues) ||
    (playType !== "fieldGoal" && !hasAllRelatedValues);

  if (dataIsMissing || isPlayInterruption(playType)) {
    return null;

    // field goals account for the length of the end zone and hiking the ball:
    // the ball is typically hiked 7 yards
    // the end zone is 10 yards deep and the goal post is at the back of the end zone
  } else if (playType === "fieldGoal" && includesScore) {
    // really long kick probably wont happen but just in case
    const isReallyLongKick = teamInPossessionId === yardLineTeamId;
    return isReallyLongKick ? 50 + (50 - yardLine) + 17 : yardLine + 17;
  } else if (playType === "fieldGoal" && !includesScore) {
    return 0;
  } else if (
    teamInPossessionId === yardLineTeamId &&
    teamInPossessionId === ballSpotTeamId
  ) {
    return ballSpot - yardLine;
  } else if (yardLineTeamId === ballSpotTeamId) {
    return yardLine - ballSpot;
  } else if (playType === "touchback") {
    return 0;
  } else {
    const rawLength = 50 - yardLine + (50 - ballSpot);
    return teamInPossessionId === yardLineTeamId ? rawLength : rawLength * -1;
  }
};

const getPlayerId = (fieldName, play, playerName) => {
  return fieldName === "teamInPossessionId" ? null : _.get(play, playerName);
};

const getIncludesScore = (fieldName, fieldValue, play) => {
  return fieldName === "PATBlocked" && fieldValue
    ? !fieldValue
    : play.includesScore;
};

const getDistanceToGo = (fieldName, fieldValue, play) => {
  if (fieldName === "isGoalDistanceToGo") {
    return fieldValue ? 100 : null;
  } else {
    return play.distanceToGo;
  }
};

const midPlayChanges = (fieldName, fieldValue, play) => {
  if (fieldName === "playType") return {};
  const playType = _.get(play, "playType");

  const player1Id = getPlayerId(fieldName, play, "player1Id");
  const player2Id = getPlayerId(fieldName, play, "player2Id");
  const includesScore = getIncludesScore(fieldName, fieldValue, play);
  const distanceToGo = getDistanceToGo(fieldName, fieldValue, play);
  const lengthOfPlay = getLengthOfPlay(fieldName, play);
  const convertsFirstDown = getconvertsFirstDown(
    fieldName,
    distanceToGo,
    lengthOfPlay,
    play
  );

  return {
    ...(fieldExistsOnPlayType(playType, "lengthOfPlay") && { lengthOfPlay }),
    ...(fieldExistsOnPlayType(playType, "player1Id") && { player1Id }),
    ...(fieldExistsOnPlayType(playType, "player2Id") && { player2Id }),
    ...(fieldExistsOnPlayType(playType, "includesScore") && { includesScore }),
    ...(fieldExistsOnPlayType(playType, "distanceToGo") && { distanceToGo }),
    ...(fieldExistsOnPlayType(playType, "convertsFirstDown") && {
      convertsFirstDown
    })
  };
};

export { midPlayChanges, getconvertsFirstDown };
